<template>
    <div class="my-page">
        <!-- 顶部用户信息 -->
        <div class="user-info">
            <div class="user-info-content">
                <div class="user-avatar">
                    <img v-if="showAvatar" :src="userAvatar" alt="avatar" @error="avatarLoadError = true">
                    <div v-else class="default-avatar">
                        <van-icon name="manager-o" />
                    </div>
                </div>
                <div class="user-details">
                    <div class="user-name">{{ userInfo.name}}</div>
                    <div class="user-welcome">欢迎登录！</div>
                </div>
                <div class="edit-btn" @click="goToProfile">
                    <van-icon name="edit" size="20" color="#fff" />
                </div>
            </div>
        </div>

        <!-- 标签页 -->
        <div class="content-tabs">
            <van-tabs v-model="activeTab" color="#333" title-active-color="#333" title-inactive-color="#999">
                <van-tab title="考试记录" name="exam">
                    <div class="exam-list">
                        <div v-if="!loading && finished && examRecords.length === 0" class="empty-state">
                            <van-empty description="暂无考试记录" />
                        </div>
                        <van-list
                            v-else
                            v-model="loading"
                            :finished="finished"
                            @load="onLoad"
                        >
                            <div 
                                class="exam-record-item" 
                                :class="{ 'clickable': item.totalScore >= 0 }"
                                v-for="(item, index) in examRecords" 
                                :key="item.id || index"
                                @click="handleExamRecordClick(item)"
                            >
                                <div class="exam-record-header">
                                    <van-icon name="records" color="#00BCD4" size="16" />
                                    <span class="exam-record-title">{{ item.testPaperTitle }}</span>
                                </div>
                                <div class="exam-record-info">
                                    <div class="info-item">
                                        <span class="label">考试时间</span>
                                        <span class="value">{{ item.startTime }}</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="label">考试用时</span>
                                        <span class="value">{{ calculateDuration(item.startTime, item.endTime) }}</span>
                                    </div>
                                    <div class="info-item">
                                        <span class="label">得分</span>
                                        <div v-if="item.state === '待批阅'" class="score-badge score-pending">
                                            待批阅
                                        </div>
                                        <div v-else class="score-badge" :class="getScoreClass(item.totalScore)">
                                            {{ item.totalScore }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </van-list>
                    </div>
                </van-tab>
                <van-tab title="培训证明" name="training">
                    <div class="training-list">
                        <div v-if="trainingLoading" class="loading-state">
                            <van-loading type="spinner" color="#00BCD4">加载中...</van-loading>
                        </div>
                        <div v-else-if="trainingList.length === 0" class="empty-state">
                            <van-empty description="暂无培训证明" />
                        </div>
                        <div v-else class="training-item" v-for="(item, index) in trainingList" :key="item.id || index">
                            <div class="training-header">
                                <img class="training-icon" src="~@/assets/image/training-icon.png" alt="">
                                <span class="training-title">{{ item.title || item.testPaperTitle || '' }}</span>
                            </div>
                            <div class="training-info">
                                <div class="training-status">
                                    <img class="training-pass-icon" src="~@/assets/image/training-pass-icon.png" alt="">
                                    <span class="status-text">考试已通过</span>
                                </div>
                                <!-- <van-button 
                                    type="info" 
                                    size="small"
                                    round
                                    @click="viewCertificate(item)"
                                >
                                    查看证书
                                </van-button> -->
                            </div>
                        </div>
                    </div>
                </van-tab>
            </van-tabs>
        </div>
    </div>
</template>

<script>
    import { getMyAnswers, getLoginInfo, getTrainList } from '@/api/index.js';
    import moment from 'moment';
    
    export default {
        components: {},
        props: {},
        data() {
            return {
                activeTab: 'exam',
                examRecords: [],
                pageNum: 1,
                pageSize: 20,
                loading: false,
                finished: false,
                trainingList: [],
                trainingLoading: false,
                avatarLoadError: false
            };
        },
        watch: {
            userAvatar() {
                this.avatarLoadError = false;
            },
            activeTab(newVal) {
                if (newVal === 'exam' && this.examRecords.length === 0) {
                    // 重置分页状态
                    this.pageNum = 1;
                    this.finished = false;
                    this.loadExamRecords(false);
                } else if (newVal === 'training' && this.trainingList.length === 0) {
                    // 切换到培训证明标签页时加载数据
                    this.loadTrainingList();
                }
            }
        },
        computed: {
            // 从 Vuex 中获取用户信息
            userInfo() {
                return this.$store.state.userInfo || {
                    name: '',
                    avatar: ''
                };
            },
            userAvatar() {
                return (this.userInfo.avatar || '').trim();
            },
            showAvatar() {
                return !!this.userAvatar && !this.avatarLoadError;
            }
        },
        created() {
            this.loadUserInfo();
        },
        mounted() {
            this.loadExamRecords(false);
        },
        methods: {
            loadUserInfo() {
                getLoginInfo().then(res => {
                    if (res.data) {
                        this.$store.commit('setUserInfo', {
                            id: res.data.id || '',
                            name: res.data.name || '',
                            account: res.data.account || '',
                            avatar: res.data.avatar || '',
                            idCard: res.data.idCard || '',
                            phone: res.data.phone || '',
                            hospital: res.data.hospital || '',
                            type: res.data.type || ''
                        });
                    }
                });
            },
            onLoad() {
                // 下拉加载更多
                this.loadExamRecords(true);
            },
            loadExamRecords(isLoadMore = false) {
                if (this.loading || this.finished) return;
                
                this.loading = true;
                if (!isLoadMore) {
                    this.pageNum = 1;
                    this.finished = false;
                }
                const data = {
                    pageNum: this.pageNum,
                    pageSize: this.pageSize
                };
                
                getMyAnswers(data).then(res => {
                    this.loading = false;
                    
                    const records = res.data?.records || [];
                    const total = res.data?.total || 0;
                    
                    if (isLoadMore) {
                        // 追加数据
                        this.examRecords = [...this.examRecords, ...records];
                    } else {
                        // 首次加载
                        this.examRecords = records;
                    }
                    
                    // 判断是否加载完成
                    if (this.examRecords.length >= total || records.length < this.pageSize) {
                        this.finished = true;
                    } else {
                        this.pageNum++;
                    }
                }).catch(err => {
                    this.loading = false;
                    this.finished = true;
                });
            },
            calculateDuration(startTime, endTime) {
                if (!startTime || !endTime) {
                    return '--';
                }
                
                const start = moment(startTime);
                const end = moment(endTime);
                
                if (!start.isValid() || !end.isValid()) {
                    return '--';
                }
                
                const duration = moment.duration(end.diff(start));
                const hours = Math.floor(duration.asHours());
                const minutes = duration.minutes();
                const seconds = duration.seconds();
                
                if (hours > 0) {
                    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
                } else {
                    return `${minutes}:${String(seconds).padStart(2, '0')}`;
                }
            },
            getScoreClass(score) {
                if (score >= 90) return 'score-excellent';
                if (score >= 80) return 'score-good';
                if (score >= 60) return 'score-pass';
                return 'score-fail';
            },
            goToProfile() {
                this.$router.push('/profile');
            },
            loadTrainingList() {
                if (this.trainingLoading) return;
                
                this.trainingLoading = true;
                getTrainList().then(res => {
                    this.trainingLoading = false;
                    this.trainingList = res.data || [];
                }).catch(err => {
                    this.trainingLoading = false;
                });
            },
            viewCertificate(item) {
                if (!item.answerId) {
                    this.$toast('缺少答卷ID');
                    return;
                }
                
                // 跳转到证书查看页面
                this.$router.push({ 
                    path: '/my/certificate', 
                    query: { answerId: item.answerId } 
                });
            },
            handleExamRecordClick(item) {
                
                if (!item.id) {
                    this.$toast('缺少答卷ID');
                    return;
                }
                
                // 跳转到考试记录详情页
                this.$router.push({
                    path: '/my/exam-result-detail',
                    query: { answerId: item.id }
                });
            }
        },
    };
</script>

<style scoped lang='scss'>
.my-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: linear-gradient(to bottom, #C2E5EB, #EEF4F5);
    overflow-x: hidden;
}

.user-info {
    position: relative;
    box-sizing: border-box;
    padding: 25px 15px;
    width: 100%;
    height: 211px;
    background: url('~@/assets/image/my-top-bg.png') no-repeat center center;
    background-size: 100% 100%;
}
.user-info-content{
    display: flex;
    align-items: center;
    height: 45px;
    .user-avatar {
        width: 45px;
        height: 45px;
        border-radius: 50%;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.5);
        flex-shrink: 0;
        
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
        }

        .default-avatar {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.92);
            color: #0A578A;
            font-size: 26px;
        }
    }
    .user-details {
        margin-left: 7px;
        flex: 1;
        .user-name {
            font-size: 15px;
            color: #fff;
            margin-bottom: 4px;
        }
        .user-welcome {
            font-size: 12px;
            color: rgba(255, 255, 255, 0.6);
            cursor: pointer;
            
            &:active {
                opacity: 0.8;
            }
        }
    }
    .edit-btn {
        width: 40px;
        height: 40px;
        background: #0A578A;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        &:active {
            background: rgba(255, 255, 255, 0.3);
        }
    }

}

.content-tabs {
    margin-top: -100px;
    flex: 1;
    min-height: 0;
    box-sizing: border-box;
    position: relative;
    border-radius: 20px 20px 0 0;
    padding-top: 10px;
    background: linear-gradient(to bottom, #C2E5EB, #EEF4F5);
    z-index: 2;
    overflow: visible;
    
    ::v-deep .van-tabs__wrap {
        padding: 0 15px;
    }

    ::v-deep .van-tabs {
        height: 100%;
    }

    ::v-deep .van-tabs__content {
        overflow: visible;
    }
    
    ::v-deep .van-tabs__nav {
        background: transparent;
    }
    
    ::v-deep .van-tabs__line {
        width: 30px;
        height: 3px;
        background: #FF6633 !important;
    }
    
    ::v-deep .van-tab {
        font-size: 16px;
        font-weight: 500;
    }
}

.exam-list {
    padding: 5px 15px 20px;
}

.training-list {
    padding: 5px 15px 20px;
}

.training-item {
    background: #fff;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 15px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    
    &:last-child {
        margin-bottom: 0;
    }
}

.training-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    
    .training-icon {
        width: 24px;
        height: 24px;
        object-fit: contain;
    }
}

.training-title {
    font-size: 15px;
    color: #333;
    font-weight: 500;
}

.training-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.training-status {
    display: flex;
    align-items: center;
    gap: 5px;
    
    .training-pass-icon {
        width: 12px;
        height: 14px;
        object-fit: contain;
    }
}

.status-text {
    font-size: 12px;
    color: #999999;
}

.training-info .van-button {
    padding: 0 16px;
    height: 28px;
    font-size: 12px;
    background: #46CCDC;
    border-color: #46CCDC;
}

.exam-record-item {
    background: #fff;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 15px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    
    &:last-child {
        margin-bottom: 0;
    }
    
    &.clickable {
        cursor: pointer;
        transition: all 0.3s ease;
        
        &:active {
            transform: scale(0.98);
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
        }
    }
}

.exam-record-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f5f5f5;
}

.exam-record-title {
    font-size: 15px;
    color: #333;
    font-weight: 500;
}

.exam-record-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.info-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 14px;
}

.label {
    color: #999;
}

.value {
    color: #333;
}

.score-badge {
    min-width: 40px;
    height: 28px;
    padding: 0 12px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: bold;
    color: #fff;
}

.score-excellent {
    background: linear-gradient(135deg, #00D4FF, #00A8CC);
}

.score-good {
    background: linear-gradient(135deg, #4FC3F7, #29B6F6);
}

.score-pass {
    background: linear-gradient(135deg, #FFB74D, #FF9800);
}

.score-fail {
    background: linear-gradient(135deg, #FF7043, #FF5722);
}

.score-pending {
    background: linear-gradient(135deg, #FFB74D, #FF9800);
    color: #fff;
}

.empty-state {
    padding: 60px 20px;
    text-align: center;
    
    .empty-text {
        font-size: 14px;
        color: #999;
    }
}

.loading-state {
    padding: 60px 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}
</style>
